import { Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import {
  VEHICLE_CFD_STATUS_BUCKET_LABELS,
  cfdTileCountBuckets,
  type VehicleCfdStatusBucket
} from '../../vehicle-maintenance/vehicle-cfds/utils/vehicle-cfd-status.util';
import { isCanadaRegion } from '../dashboard-dates.util';
import type { DashboardVehicleTile } from '../dashboard.types';

interface CfdCountRow {
  bucket: VehicleCfdStatusBucket;
  label: string;
  /** Full status name, shown on hover since the tile labels are abbreviated to fit. */
  title: string;
  count: number;
}

/** The two Upload variants are too long for a tile; the tooltip carries the full status name. */
const SHORT_LABELS: Partial<Record<VehicleCfdStatusBucket, string>> = {
  unpublishedChanges: 'Unpublished Changes',
  pendingTranslation: 'Pending Translation'
};

/**
 * CFD progress for one vehicle. Counts come from the server-side `vehicleCfdSummaries` aggregate and
 * each links into the CFD list pre-filtered to that status. The parent only renders this when
 * `hasCfds` is true (any status count > 0; "--" only after model trims are linked).
 */
@Component({
  selector: 'app-cfd-tile',
  imports: [MatIconModule, MatTooltipModule, RouterLink],
  templateUrl: './cfd-tile.component.html',
  styleUrl: './cfd-tile.component.scss'
})
export class CfdTileComponent {
  readonly tile = input.required<DashboardVehicleTile>();

  readonly copyCfds = output<DashboardVehicleTile>();

  readonly vehicleId = computed(() => this.tile().vehicle.id);
  readonly cfdListLink = computed(() => ['/maintenance', this.vehicleId(), 'cfds']);
  readonly modelTrimLinksLink = computed(() => [
    '/maintenance',
    this.vehicleId(),
    'model-trim-links'
  ]);

  readonly reportQueryParams = computed(() => ({
    vehicleId: this.vehicleId(),
    year: this.tile().vehicle.vehicleYear ?? undefined
  }));

  readonly isComplete = computed(() => this.tile().cfd?.isComplete === true);

  /** Null until the summary arrives, which the template renders as a loading row. */
  readonly counts = computed<CfdCountRow[] | null>(() => {
    const summary = this.tile().cfd;
    if (!summary) {
      return null;
    }
    const buckets = cfdTileCountBuckets(isCanadaRegion(this.tile().vehicle.regionName));
    return buckets.map((bucket) => ({
      bucket,
      label: SHORT_LABELS[bucket] ?? VEHICLE_CFD_STATUS_BUCKET_LABELS[bucket],
      title: VEHICLE_CFD_STATUS_BUCKET_LABELS[bucket],
      count: summary[bucket]
    }));
  });

  readonly totalRpos = computed(() => this.tile().cfd?.totalRpos ?? 0);

  onCopyCfds(): void {
    this.copyCfds.emit(this.tile());
  }
}
